// src/ai/estimators/listingTextEstimator.js

/**
 * Build suggested title, short description, long description and tags
 * from the current listing draft. Deterministic heuristic; swap with AI later.
 * @param {Object} draft
 */
export function estimateListingText(draft) {
  if (!draft) {
    return {
      title: "",
      shortDescription: "",
      longDescription: "",
      tags: [],
      notes: ["Fill in some basic details first (name, category, clay, finish, size)."],
    };
  }

  const {
    name,
    category,
    clayBody,
    glazeName,
    surfaceFinish,
    sizeLabel,
    capacityMl,
    intendedUse,
    styleTone,
    studioName,
  } = normalizeDraft(draft);

  const parts = [];

  if (styleTone === "calm") {
    parts.push("Soft, wheel-thrown");
  } else if (styleTone === "rustic") {
    parts.push("Warm, hand-crafted");
  } else {
    parts.push("Wheel-thrown");
  }

  if (clayBody) parts.push(clayBody);
  if (glazeName) parts.push(glazeName);

  const baseKind = category || name || "ceramic piece";
  const niceKind = baseKind.toLowerCase().includes("mug")
    ? "mug"
    : baseKind.toLowerCase().includes("bowl")
    ? "bowl"
    : baseKind.toLowerCase().includes("plate")
    ? "plate"
    : baseKind.toLowerCase();

  const titleParts = [
    surfaceFinish ? surfaceFinish : null,
    glazeName || null,
    niceKind ? capitalizeFirst(niceKind) : null,
  ].filter(Boolean);

  const suggestedTitle = titleParts.length > 0 ? titleParts.join(" ") : name || "Studio piece";

  const shortBits = [];
  if (intendedUse) {
    shortBits.push(`Perfect for ${intendedUse.toLowerCase()}.`);
  } else if (niceKind.includes("mug")) {
    shortBits.push("Comfortable in the hand and sized for everyday coffee or tea.");
  } else if (niceKind.includes("bowl")) {
    shortBits.push("Great for breakfast, noodles or generous salads.");
  }

  if (capacityMl) {
    shortBits.push(`Approx. ${capacityMl} ml capacity.`);
  } else if (sizeLabel) {
    shortBits.push(sizeLabel);
  }

  const shortDescription =
    shortBits.length > 0 ? shortBits.join(" ") : "Everyday piece for the table or shelf.";

  const longLines = [];

  longLines.push(
    `${capitalizeFirst(styleToneLabel(styleTone))} ${niceKind || "ceramic piece"} made in small batches in the studio${
      studioName ? " at " + studioName : ""
    }.`
  );

  if (clayBody || glazeName || surfaceFinish) {
    longLines.push(
      [
        clayBody ? `Thrown from ${clayBody.toLowerCase()}` : null,
        glazeName ? `glazed in ${glazeName.toLowerCase()}` : null,
        surfaceFinish ? surfaceFinish.toLowerCase() + " finish" : null,
      ]
        .filter(Boolean)
        .join(", ") + "."
    );
  }

  if (intendedUse) {
    longLines.push(`Designed for ${intendedUse.toLowerCase()}.`);
  }

  if (capacityMl) {
    longLines.push(`Holds roughly ${capacityMl} ml; each piece is slightly unique.`);
  } else if (sizeLabel) {
    longLines.push(sizeLabel);
  }

  longLines.push(
    "Each piece is individually finished; slight variations in size, glaze and texture are part of the character."
  );

  const longDescription = longLines.join(" ");

  const tagsSet = new Set();
  if (niceKind) tagsSet.add(niceKind);
  if (category) tagsSet.add(category.toLowerCase());
  if (clayBody) tagsSet.add(clayBody.toLowerCase());
  if (glazeName) tagsSet.add(glazeName.toLowerCase());
  if (surfaceFinish) tagsSet.add(surfaceFinish.toLowerCase());
  if (intendedUse) tagsSet.add(intendedUse.toLowerCase());
  tagsSet.add("handmade");
  tagsSet.add("ceramics");

  const tags = Array.from(tagsSet).slice(0, 10);

  const notes = [
    "Review and tweak wording so it sounds like you.",
    "You can still edit title, descriptions and tags after applying these suggestions.",
  ];

  return {
    title: suggestedTitle,
    shortDescription,
    longDescription,
    tags,
    notes,
  };
}

function normalizeDraft(draft) {
  return {
    name: draft.name || "",
    category: draft.category || draft.productType || "",
    clayBody: draft.clayBody || draft.clay || "",
    glazeName: draft.glazeName || draft.glaze || "",
    surfaceFinish: draft.surfaceFinish || draft.finish || "",
    sizeLabel: draft.sizeLabel || draft.sizeText || "",
    capacityMl: draft.capacityMl || draft.volumeMl || null,
    intendedUse: draft.intendedUse || draft.useCase || "",
    styleTone: draft.styleTone || "calm", // calm | rustic | bold
    studioName: draft.studioName || "",
  };
}

function capitalizeFirst(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function styleToneLabel(styleTone) {
  if (styleTone === "rustic") return "Earthy, rustic";
  if (styleTone === "bold") return "Bold, statement";
  return "Soft, everyday";
}
